import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import axiosInstance from '../../api/axiosInstance';
import { toast } from 'react-toastify';

const SELECT_MENU_STYLE = {
  margin: 0,
  padding: '6px',
  listStyle: 'none',
  background: 'var(--surface-elevated, #fff)',
  border: '1px solid var(--border, #e2e8f0)',
  borderRadius: '12px',
  boxShadow: 'var(--shadow-lg, 0 12px 24px rgba(15, 23, 42, 0.12))',
  overflowY: 'auto',
  maxHeight: '240px',
  zIndex: 20000,
  boxSizing: 'border-box',
};

/**
 * Custom select with portal menu — matches RunTestCaseModal pattern.
 * Width stays locked to the trigger; height is capped with scroll.
 */
const RunModalSelect = ({
  id,
  value,
  onChange,
  options,
  disabled = false,
  placeholder = 'Select...',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState(null);

  const updateMenuPosition = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const menuMaxHeight = 240;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openAbove = spaceBelow < menuMaxHeight && rect.top > spaceBelow;

    setMenuPosition({
      position: 'fixed',
      top: openAbove ? rect.top - 4 : rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      transform: openAbove ? 'translateY(-100%)' : 'none',
    });
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    updateMenuPosition();
    window.addEventListener('scroll', updateMenuPosition, true);
    window.addEventListener('resize', updateMenuPosition);

    return () => {
      window.removeEventListener('scroll', updateMenuPosition, true);
      window.removeEventListener('resize', updateMenuPosition);
    };
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      const target = event.target;
      if (
        triggerRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }
      setIsOpen(false);
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const selectedOption = options.find(
    (option) => String(option.value) === String(value)
  );

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <>
      <button
        type="button"
        id={id}
        ref={triggerRef}
        disabled={disabled}
        onClick={() => !disabled && setIsOpen((open) => !open)}
        className="rt-select__trigger w-full min-w-0 max-w-full"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.5rem',
          textAlign: 'left',
          cursor: disabled ? 'not-allowed' : 'pointer',
          minWidth: 0,
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
            flex: 1,
            color: selectedOption && selectedOption.value !== '' ? '#111827' : '#6b7280',
          }}
        >
          {selectedOption?.label || placeholder}
        </span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          style={{ flexShrink: 0, color: '#9ca3af' }}
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {isOpen &&
        menuPosition &&
        createPortal(
          <ul
            ref={menuRef}
            role="listbox"
            aria-labelledby={id}
            style={{ ...SELECT_MENU_STYLE, ...menuPosition }}
          >
            {options.map((option) => {
              const isSelected = String(value) === String(option.value);
              return (
                <li
                  key={option.value === '' ? '__empty__' : String(option.value)}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => handleSelect(option.value)}
                  style={{
                    padding: '0.5rem 1rem',
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    backgroundColor: isSelected ? '#eff6ff' : 'transparent',
                    color: isSelected ? '#1d4ed8' : '#111827',
                    fontWeight: isSelected ? 500 : 400,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = isSelected ? '#eff6ff' : 'transparent';
                  }}
                >
                  {option.label}
                </li>
              );
            })}
          </ul>,
          document.body
        )}
    </>
  );
};

/**
 * RunTestSuiteModal Component
 * Modal for running a test suite with:
 * - Environment selection
 * - Local Machine (via browser extension)
 * - Server (headless, via API)
 */
const RunTestSuiteModal = ({
  isOpen,
  onClose,
  onSuccess,
  testSuiteId,
  testSuiteName,
  projectId,
  environmentId,
}) => {
  const [selectedMode, setSelectedMode] = useState(null);
  const [selectedEnvironmentId, setSelectedEnvironmentId] = useState('');
  const [environments, setEnvironments] = useState([]);
  const [environmentsLoading, setEnvironmentsLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchEnvironments = useCallback(async (envProjectId) => {
    if (!envProjectId) {
      setEnvironments([]);
      return;
    }

    try {
      setEnvironmentsLoading(true);
      const response = await axiosInstance.get(
        `/user_profile/list_environments?project_id=${envProjectId}`
      );

      if (response.data && response.status_code === 200) {
        setEnvironments(response.data || []);
      } else {
        console.error('Failed to fetch environments:', response);
        setEnvironments([]);
      }
    } catch (err) {
      console.error('Error fetching environments:', err);
      setEnvironments([]);
    } finally {
      setEnvironmentsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen && projectId) {
      fetchEnvironments(projectId);
    }
  }, [isOpen, projectId, fetchEnvironments]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedMode(null);
      setSelectedEnvironmentId('');
      setError(null);
      return;
    }
    setSelectedMode('local');
    setError(null);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (selectedEnvironmentId) return;
    if (environmentsLoading) return;
    if (!environments || environments.length === 0) return;
    if (environmentId == null || environmentId === '') return;

    const match = environments.find(
      (env) => String(env.id) === String(environmentId)
    );
    if (match) {
      setSelectedEnvironmentId(String(match.id));
      setError(null);
    }
  }, [isOpen, environmentId, environments, environmentsLoading, selectedEnvironmentId]);

  const selectedEnvironment = environments.find(
    (env) => env.id === parseInt(selectedEnvironmentId, 10)
  );

  const environmentOptions = [
    {
      value: '',
      label: environmentsLoading ? 'Loading environments...' : 'Select an environment',
    },
    ...environments.map((env) => ({
      value: String(env.id),
      label: env.environment_name,
    })),
  ];

  const handleRun = async () => {
    if (!selectedEnvironmentId) {
      setError('Please select an environment');
      return;
    }

    if (!selectedMode) {
      setError('Please select an execution mode');
      return;
    }

    if (!testSuiteId) {
      setError('No test suite selected');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      if (selectedMode === 'local') {
        const message = {
          source: 'ROBITEST_CLOUD',
          action: 'RUN_TEST_SUITES',
          test_suite_ids: [testSuiteId],
          project_id: parseInt(projectId, 10) || projectId,
          environment_id: selectedEnvironment?.id ?? parseInt(selectedEnvironmentId, 10),
          environment_url: selectedEnvironment?.target_url || '',
        };

        if (window.parent !== window) {
          window.parent.postMessage(message, '*');
        } else {
          window.postMessage(message, '*');
        }

        onSuccess?.();
        toast.info(`Starting test suite execution...`);
        setTimeout(() => {
          setIsLoading(false);
          resetModal();
          onClose();
        }, 500);
      } else if (selectedMode === 'server') {
        const envId = selectedEnvironment?.id ?? parseInt(selectedEnvironmentId, 10);
        const response = await axiosInstance.post(
          `/execute_file/execute_test_suite?test_suite_id=${testSuiteId}&environment_id=${envId}`
        );

        if (response?.status === 202 || response?.status_code === 202) {
          toast.success(response.message || 'Test suite execution started on Server');
          setIsLoading(false);
          resetModal();
          onClose();
          onSuccess?.();
        } else {
          throw new Error(response?.message || 'Failed to execute test suite');
        }
      }
    } catch (err) {
      console.error('Error executing test suite:', err);
      const errorMessage =
        err.response?.data?.message || err.message || 'Failed to execute test suite';
      toast.error(errorMessage);
      setIsLoading(false);
    }
  };

  const resetModal = () => {
    setSelectedMode(null);
    setSelectedEnvironmentId('');
    setError(null);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  if (!isOpen) return null;

  const canRun =
    Boolean(selectedEnvironmentId) && Boolean(selectedMode) && !environmentsLoading;

  return (
    <div data-rt-modal-overlay className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
      style={{ zIndex: 13000 }}
     onClick={(e) => { if (e.target === e.currentTarget) { const b = e.currentTarget.querySelector('[data-rt-modal-close], .close-btn, .modal-close, button[aria-label="Close"], button[aria-label="close"]'); if (b) b.click(); } }}>
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col animate-in fade-in zoom-in duration-200"
        style={{ zIndex: 14000 }}
      >
        {/* Header */}
        <div className="rt-modal__header flex-shrink-0">
          <div>
            <h2>Run Test Suite: {testSuiteName}</h2>
            <p>Select where to run this test suite</p>
          </div>
        </div>

        {/* Body - Scrollable */}
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {/* Environment Dropdown */}
          <div className="space-y-2">
            <label htmlFor="runTestSuiteEnvironment" className="block text-sm font-semibold text-gray-700">
              Environment <span className="text-red-500">*</span>
            </label>
            <RunModalSelect
              id="runTestSuiteEnvironment"
              value={selectedEnvironmentId}
              onChange={(value) => {
                setSelectedEnvironmentId(value);
                setError(null);
              }}
              options={environmentOptions}
              placeholder={
                environmentsLoading ? 'Loading environments...' : 'Select an environment'
              }
              disabled={isLoading || environmentsLoading}
            />
            {selectedEnvironment && (
              <p className="text-xs text-blue-600 flex items-start gap-1 min-w-0">
                <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="min-w-0 break-all">URL: {selectedEnvironment.target_url}</span>
              </p>
            )}
          </div>

          {/* Execution Mode Selection */}
          <div className="space-y-3">
            <label className="block text-sm font-semibold text-gray-700">
              Execution Mode <span className="text-red-500">*</span>
            </label>

            {/* Local Machine Option */}
            <label
              className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                selectedMode === 'local'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 bg-gray-50 hover:border-blue-300'
              }`}
            >
              <input
                type="radio"
                name="testSuiteExecutionMode"
                value="local"
                checked={selectedMode === 'local'}
                onChange={(e) => {
                  setSelectedMode(e.target.value);
                  setError(null);
                }}
                className="w-4 h-4 text-blue-600 cursor-pointer"
                disabled={isLoading}
              />
              <div className="ml-3 flex-1">
                <p className="font-medium text-gray-700">Local Machine (Live View)</p>
                <p className="text-xs text-gray-600 mt-1">
                  Run test suite via browser extension on your local machine
                </p>
              </div>
              <svg
                className="w-6 h-6 text-blue-600 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </label>

            {/* Server Option */}
            <label
              className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all mb-4 ${
                selectedMode === 'server'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 bg-gray-50 hover:border-blue-300'
              }`}
            >
              <input
                type="radio"
                name="testSuiteExecutionMode"
                value="server"
                checked={selectedMode === 'server'}
                onChange={(e) => {
                  setSelectedMode(e.target.value);
                  setError(null);
                }}
                className="w-4 h-4 text-blue-600 cursor-pointer"
                disabled={isLoading}
              />
              <div className="ml-3 flex-1">
                <p className="font-medium text-gray-700">Server (Headless Run)</p>
                <p className="text-xs text-gray-600 mt-1">
                  Executed on server infrastructure in headless mode
                </p>
              </div>
              <svg
                className="w-6 h-6 text-blue-600 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </label>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700 flex items-start gap-2">
                <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                {error}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t flex-shrink-0">
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="px-4 py-2 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleRun}
            disabled={isLoading || !canRun}
            className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isLoading && (
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            {isLoading ? 'Running...' : 'Run Test Suite'}
          </button>
        </div>

        {/* Info Box */}
        {selectedMode === 'local' && (
          <div className="bg-blue-50 border-t border-blue-200 px-6 py-3 flex-shrink-0">
            <p className="text-xs text-blue-700 flex items-start gap-2">
              <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <span>
                Make sure the extension is installed in your browser before running.
              </span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RunTestSuiteModal;
